(() => {
  'use strict';
function projectKit(canvas, config, variant) {
  const view = config.kitViews?.[variant?.options?.[config.setIndex]];
  if (!view) {
    delete canvas.dataset.ldsKitCount;
    return;
  }
  if (!Array.isArray(view.regions) || view.regions.length !== 2) throw new Error('Invalid reviewed kit view');
  const scratch = canvas._ldsKitScratch || (canvas._ldsKitScratch = document.createElement('canvas'));
  scratch.width = canvas.width;
  scratch.height = canvas.height;
  const copy = scratch.getContext('2d');
  copy.drawImage(canvas, 0, 0);
  const context = canvas.getContext('2d');
  context.fillStyle = '#000';
  context.fillRect(0, 0, canvas.width, canvas.height);
  for (const region of view.regions) {
    if (!Array.isArray(region) || region.length !== 6 || !region.every(Number.isFinite)) throw new Error('Invalid kit region');
    const [x, y, width, height, dx, dy] = region;
    if (x < 0 || y < 0 || width <= 0 || height <= 0 || x + width > canvas.width || y + height > canvas.height || dx < 0 || dy < 0 || dx + width > canvas.width || dy + height > canvas.height) throw new Error('Kit region outside source');
    context.drawImage(scratch, x, y, width, height, dx, dy, width, height);
  }
  canvas.dataset.ldsKitCount = String(view.count);
}


  const TAG = 'lds-quick-preview';
  if (customElements.get(TAG)) return;

  // These values and the renderer's pixel composition match the accepted sill
  // color pilot. The adapter deliberately has no picker, modal, or gallery
  // controls of its own; the native quick view remains the owner of those.
  const LIGHT_COLORS = {
    WHITE: [255, 255, 255],
    RED: [255, 38, 48],
    BLUE: [46, 70, 245],
    GREEN: [42, 210, 99],
    YELLOW: [255, 217, 42],
    'ICE BLUE': [76, 216, 255],
    PURPLE: [181, 79, 255],
    ORANGE: [255, 128, 24],
  };
  const BODY_COLORS = {
    WHITE: [244, 244, 239],
    RED: [211, 38, 45],
    BLUE: [40, 76, 181],
    GREEN: [43, 140, 77],
    YELLOW: [238, 201, 38],
    ORANGE: [229, 112, 30],
  };
  const BADGE_STATIC_COLORS = {
    WHITE: [255, 255, 255],
    RED: [255, 38, 48],
    BLUE: [46, 70, 245],
    GREEN: [42, 210, 99],
    YELLOW: [255, 217, 42],
    'ICE BLUE': [76, 216, 255],
    PURPLE: [181, 79, 255],
  };
  const BADGE_FLOW_ALIASES = new Set(['FLOW', 'RGB', 'FLOW RGB', 'RGB FLOW', 'FLOW-RGB', 'RGB-FLOW']);
  const IMAGE_SLIDE_SELECTOR = '.hdt-product-media__main [data-media-type="image"]';
  const normalize = value => String(value ?? '').trim().replace(/\s+/g, ' ').toUpperCase();
  const integer = value => Number.isInteger(Number(value)) ? Number(value) : null;
  const finite = value => Number.isFinite(Number(value)) ? Number(value) : null;
  const normalizeFlowValue = value => normalize(value).replace(/[_/]+/g, ' ').replace(/\s*[-+]\s*/g, '-').replace(/\s+/g, ' ').trim();
  const badgeColor = value => {
    const key = normalizeFlowValue(value);
    if (BADGE_FLOW_ALIASES.has(key)) return 'FLOW';
    if (key === 'ICEBLUE') return 'ICE BLUE';
    return BADGE_STATIC_COLORS[key] ? key : null;
  };

  const imageFromUrl = (url, exactPNG=false) => {
    if(exactPNG)return fetch(new URL(String(url),document.baseURI).href,{mode:'cors',credentials:'omit',headers:{Accept:'image/png'}}).then(async response=>{
      if(!response.ok||!/^image\/png(?:;|$)/i.test(response.headers.get('content-type')||''))throw new Error('Exact Full PNG unavailable');
      const bytes=await response.arrayBuffer(),signature=new Uint8Array(bytes,0,Math.min(8,bytes.byteLength));
      if(signature.length!==8||![137,80,78,71,13,10,26,10].every((value,index)=>signature[index]===value))throw new Error('Invalid Full PNG');
      const objectURL=URL.createObjectURL(new Blob([bytes],{type:'image/png'}));
      return new Promise((resolve,reject)=>{const image=new Image();image.onload=()=>{URL.revokeObjectURL(objectURL);resolve(image)};image.onerror=()=>{URL.revokeObjectURL(objectURL);reject(new Error('Full PNG decode failed'))};image.src=objectURL;});
    });
    return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.decoding = 'async';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('preview image failed'));
    image.src = new URL(String(url), document.baseURI).href;
  });};

  const hue = h => {
    const channel = n => {
      const k = (n + h * 12) % 12;
      return Math.round(255 * (0.57 - 0.43 * Math.max(-1, Math.min(k - 3, 9 - k, 1))));
    };
    return [channel(0), channel(8), channel(4)];
  };

  class QuickPreview extends HTMLElement {
    constructor() {
      super();
      this._generation = 0;
      this._started = false;
      this._raf = 0;
      this._flowRetry = 0;
      this._lastFrame = -Infinity;
      this._badgeRevision = 0;
      this._kind = 'sill';
    }

    connectedCallback() {
      if (this._started) return;
      this._started = true;
      this._badgeClosing = false;
      this._generation += 1;
      this._abort = new AbortController();
      this._fallback('loading');
      queueMicrotask(() => this._connect(this._generation));
    }

    disconnectedCallback() {
      this._generation += 1;
      this._started = false;
      this._teardown();
    }

    _connect(generation) {
      if (!this.isConnected || generation !== this._generation) return;

      this._dialog = this.closest('#hdt-quick-view-modal');
      this._scope = this.closest('.hdt-main-section') || this.closest('.hdt-main-product-quick-view') || this.parentElement;
      if (!(this._dialog instanceof HTMLDialogElement) || !this._scope) {
        this._fallback('outside-native-quick-view');
        return;
      }

      try {
        const node = this.querySelector('[data-lds-quick-preview-config]');
        this._config = JSON.parse(node?.textContent?.trim() || 'null');
      } catch {
        this._fallback('invalid-config');
        return;
      }
      this._kind = normalize(this.dataset.ldsPreviewKind || this._config?.kind || 'sill').toLowerCase();
      this._kitBaseConfig=this._config;this._kitAssetKey='__BASE__';this._kitAssetRevision=0;this._kitAssetCache=new Map();
      if (this._kind === 'badge') {
        this._config = this._normalizeBadgeConfig(this._config);
        if (!this._config) {
          this._fallback('badge-disabled');
          return;
        }
      }
      if (!this._config || !this._config.source || !this._config.mask) {
        this._fallback('missing-config');
        return;
      }
      if (this._config.productId && this.dataset.productId && String(this._config.productId) !== String(this.dataset.productId)) {
        this._fallback('product-mismatch');
        return;
      }

      this._media = this._scope.querySelector('hdt-product-media');
      this._form = this._findForm();
      this._picker = this._findPicker();
      if (this._kind === 'badge' && this._config.colorIndex === null) {
        this._config.colorIndex = this._findBadgeColorIndex();
      }
      this._slides = [...(this._media?.querySelectorAll(IMAGE_SLIDE_SELECTOR) || [])];
      this._slide = this._findSourceSlide();
      this._sourceImage = this._slide?.querySelector('img');
      if (!this._media || !this._slide || !this._sourceImage) {
        this._fallback('source-slide-not-found');
        return;
      }

      this._frameHost = this._slide.querySelector('.hdt-product__media') || this._slide;
      this._installCanvas();
      this._bindNativeEvents();
      this._observeNativeView();
      this._variant = this._variantFromPicker();
      this._loadAssets(generation);
      this._syncVariant(this._variant);
    }

    _findForm() {
      const id = this._media?.getAttribute('form');
      if (id) {
        const form = document.getElementById(id);
        if (form) return form;
      }
      return this._scope?.querySelector('form[action*="/cart/add"], form[id^="product-form-"]') || null;
    }

    _findPicker() {
      const formId = this._media?.getAttribute('form');
      return [...(this._scope?.querySelectorAll('hdt-variant-picker') || [])]
        .find(picker => !formId || picker.getAttribute('form') === formId) || null;
    }

    _normalizeBadgeConfig(payload) {
      const wrapped = payload && payload.kind === 'badge' && Object.prototype.hasOwnProperty.call(payload, 'config');
      const raw = wrapped ? payload.config : payload;
      if (!raw || raw.enabled === false) return null;
      const legacy = !raw.source && !!raw.White && !!raw.mask;
      const source = raw.source || raw.White || null;
      const width = integer(raw.width);
      const height = integer(raw.height);
      const productOptions = Array.isArray(payload?.productOptions)
        ? payload.productOptions
        : Array.isArray(payload?.product_options)
          ? payload.product_options
          : Array.isArray(raw.productOptions)
            ? raw.productOptions
            : Array.isArray(raw.product_options) ? raw.product_options : [];
      const colorIndex = integer(raw.colorIndex) ?? this._badgeOptionIndex(productOptions);
      return {
        ...raw,
        kind: 'badge',
        legacy,
        source,
        imageSize: Array.isArray(raw.imageSize) ? raw.imageSize : [width, height],
        sourceRGB: Array.isArray(raw.sourceRGB) && raw.sourceRGB.length === 3 ? raw.sourceRGB : [255, 255, 255],
        productOptions,
        colorIndex,
        productId: raw.productId || payload?.productId || null,
      };
    }

    _badgeOptionIndex(options) {
      if (!Array.isArray(options)) return null;
      const index = options.findIndex(option => /(?:^|\s)(?:LIGHT\s+)?COLOR(?:$|\s)/i.test(String(option)));
      return index >= 0 ? index : null;
    }

    _findBadgeColorIndex() {
      const productOptions = this._config?.productOptions;
      const fromConfig = this._badgeOptionIndex(productOptions);
      if (fromConfig !== null) return fromConfig;
      const fields = [...(this._picker?.querySelectorAll('fieldset[data-index]') || [])]
        .sort((a, b) => Number(a.dataset.index) - Number(b.dataset.index));
      const index = fields.findIndex(field => {
        const text = field.querySelector('legend')?.textContent || field.dataset.name || '';
        return /(?:^|\s)(?:LIGHT\s+)?COLOR(?:$|\s)/i.test(text);
      });
      return index >= 0 ? Number(fields[index].dataset.index) : 0;
    }

    _badgeColorKey(variant = this._variant) {
      const index = integer(this._config?.colorIndex) ?? 0;
      return badgeColor(variant?.options?.[index]) || 'WHITE';
    }

    _findSourceSlide() {
      const stem = String(this._config.expectedMediaStem || '').trim().toLowerCase();
      const matching = this._slides.find(slide => [...slide.querySelectorAll('img')].some(image => {
        const url = String(image.currentSrc || image.src || '').toLowerCase();
        return !stem || url.includes(stem);
      }));
      return matching || (!stem && this._slides[0]) || null;
    }

    _installCanvas() {
      this._canvas = document.createElement('canvas');
      this._canvas.className = 'lds-quick-preview-canvas';
      this._canvas.dataset.ldsQuickPreviewCanvas = '';
      this._canvas.setAttribute('aria-hidden', 'true');
      this._canvas.hidden = true;
      this._canvas.style.position = 'absolute';
      this._canvas.style.inset = '0';
      this._canvas.style.width = '100%';
      this._canvas.style.height = '100%';
      this._canvas.style.objectFit = 'contain';
      this._canvas.style.pointerEvents = 'none';
      this._canvas.style.zIndex = '1';
      if (getComputedStyle(this._frameHost).position === 'static') {
        this._previousPosition = this._frameHost.style.position;
        this._frameHost.style.position = 'relative';
      }
      this._frameHost.append(this._canvas);
    }

    _bindNativeEvents() {
      const { signal } = this._abort;
      const onVariantChange = event => {
        this._syncVariant(event.detail?.variant || null);
        const generation = this._generation;
        this._variantFrameDeadline = performance.now() + 1000;
        requestAnimationFrame(() => {
          if (generation === this._generation && this.isConnected) this._placeVariantFrame();
        });
      };
      this._scope.addEventListener('variant:change', onVariantChange, { signal });
      if (this._kind === 'badge') {
        const closing = () => { this._badgeClosing = true; this._stopFlow(); clearTimeout(this._flowRetry); this._flowRetry = 0; };
        this._dialog.addEventListener('click', event => {
          if (event.target === this._dialog || event.target.closest?.('[ref="closeButton"]')) closing();
        }, {signal, capture:true});
        for (const name of ['cancel','dialogClosing','dialog:closing']) this._dialog.addEventListener(name, closing, {signal});
      }
      const cancelVariantFrame = () => { this._variantFrameDeadline = 0; };
      this._media.addEventListener('pointerdown', cancelVariantFrame, { signal });
      this._media.addEventListener('keydown', cancelVariantFrame, { signal });
      const onFlowCheck = () => this._scheduleFlowCheck();
      for (const eventName of [
        'dialogOpening', 'dialogOpen', 'dialogClosing', 'dialogClose',
        'dialog:opening', 'dialog:open', 'dialog:closing', 'dialog:close',
        'open', 'toggle', 'transitionend', 'animationend', 'close', 'cancel',
      ]) this._dialog.addEventListener(eventName, onFlowCheck, { signal });
      this._dialog.addEventListener('close', () => this._stopFlow(), { signal });
      document.addEventListener('visibilitychange', onFlowCheck, { signal });
    }

    _placeVariantFrame() {
      if (this._kind !== 'sill' || !this._config.fordSourceSync || !this._ready || !this._isSupported(this._variant)) return;
      const slide = this._slides.find(item => item.classList.contains('is-in-view') || item.classList.contains('is-selected') || item.classList.contains('is-active'));
      if (!slide) return;
      const host = slide.querySelector('.hdt-product__media') || slide;
      if (this._canvas.parentElement !== host) {
        if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
        host.append(this._canvas);
      }
      this._slide = slide;
      this._draw(performance.now());
    }

    _observeNativeView() {
      this._badgeWasOpen = this._dialog.open;
      this._observer = new MutationObserver(() => {
        if (this._kind === 'badge') {
          if (this._dialog.open && !this._badgeWasOpen) this._badgeClosing = false;
          this._badgeWasOpen = this._dialog.open;
        }
        if (performance.now() < this._variantFrameDeadline) this._placeVariantFrame();
        this._scheduleFlowCheck();
      });
      this._observer.observe(this._dialog, {
        attributes: true,
        attributeFilter: ['open', 'class', 'style'],
      });
      this._observer.observe(this._media, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'hidden', 'style'],
      });
    }

    _variantFromPicker() {
      const candidate = this._picker?.currentVariant || this._picker?.selectedVariant || this._picker?.variant;
      if (candidate?.options?.length) return candidate;

      const fields = [...(this._picker?.querySelectorAll('fieldset[data-index]') || [])]
        .sort((a, b) => Number(a.dataset.index) - Number(b.dataset.index));
      const options = fields.map(field => field.querySelector('[is-value]:checked, [is-value][type="hidden"]')?.value || null);
      const id = this._picker?.masterInput?.value || this._form?.querySelector('[name="id"]')?.value;
      if (!id || !options.length || options.some(option => !option)) return null;
      return { id, options };
    }

    _isSupported(variant) {
      if (this._kind === 'badge') return this._isBadgeSupported(variant);
      if (!variant?.options?.length) return false;
      const matches = this._config.previewMatch || {};
      if (!Object.entries(matches).every(([index, value]) => variant.options[Number(index)] === value)) return false;

      const color = normalize(variant.options[integer(this._config.colorIndex) ?? 0]);
      if (color !== 'FLOW' && !LIGHT_COLORS[color]) return false;
      if (!this._config.bodyMask) return true;

      const body = normalize(variant.options[integer(this._config.bodyIndex) ?? 0]);
      return body === 'BLACK' || !!BODY_COLORS[body];
    }

    _isBadgeSupported(variant) {
      if (!variant?.options?.length || this._config.enabled === false) return false;
      const color = this._badgeColorKey(variant);
      return color === 'FLOW' || !!BADGE_STATIC_COLORS[color];
    }

    _syncVariant(variant) {
      if (variant) this._variant = variant;
      else if (variant === null) this._variant = null;
      this._lastFrame = -Infinity;
      this._stopFlow();
      if(this._kind==='sill'&&this._kitBaseConfig?.kitSources){
        const key=this._isSupported(this._variant)&&this._kitBaseConfig.kitSources[this._variant?.options?.[this._kitBaseConfig.setIndex]]?this._variant.options[this._kitBaseConfig.setIndex]:'__BASE__';
        if(key!==this._kitAssetKey){this._kitAssetKey=key;this._config={...this._kitBaseConfig,...(this._kitBaseConfig.kitSources[key]||{})};this._ready=false;this._hideCanvas();this._bodyKeyApplied=null;this._loadAssets(this._generation);return;}
      }
      if (this._kind === 'badge') {
        this._syncBadgeVariant();
        return;
      }
      if (!this._ready || !this._isSupported(this._variant)) {
        this._hideCanvas();
        this._scheduleFlowCheck();
        return;
      }
      try {
        this._applyBody(this._bodyKey(this._variant));
        this._draw(performance.now());
        this._canvas.hidden = false;
        this.dataset.ldsQuickPreviewState = 'ready';
        this._scheduleFlowCheck();
      } catch {
        this._fallback('render-error');
      }
    }

    _syncBadgeVariant() {
      if (!this._ready || !this._isSupported(this._variant)) {
        this._hideCanvas();
        this._scheduleFlowCheck();
        return;
      }
      const color = this._badgeColorKey();
      const token = ++this._badgeRevision;
      try {
        if (this._config.legacy && color !== 'FLOW' && this._config[color === 'ICE BLUE' ? 'Ice Blue' : color[0] + color.slice(1).toLowerCase()]) {
          const label = color === 'ICE BLUE' ? 'Ice Blue' : color[0] + color.slice(1).toLowerCase();
          const source = this._config[label];
          this._sourceImage.hidden = false;
          this._canvas.hidden = true;
          this._sourceImage.removeAttribute('srcset');
          this._sourceImage.removeAttribute('sizes');
          this._sourceImage.src = source;
          this._sourceImage.alt = `${this._config.label || 'LED badge'} — ${label} illustrative color preview`;
          this.dataset.ldsQuickPreviewState = 'ready';
          imageFromUrl(source).then(() => {
            if (token === this._badgeRevision) this.dataset.ldsQuickPreviewState = 'ready';
          }).catch(() => {
            if (token === this._badgeRevision) this._fallback('asset-error');
          });
        } else if (color === 'WHITE' && this._badgeWhiteSource) {
          this._showBadgeOriginal();
          this.dataset.ldsQuickPreviewState = 'ready';
        } else {
          this._drawBadge(color, color === 'FLOW' ? performance.now() : null);
          this._sourceImage.hidden = true;
          this._canvas.hidden = false;
          this._canvas.setAttribute('aria-label', `${this._config.label || 'LED badge'} — ${color === 'FLOW' ? 'Flow RGB' : color} illustrative color preview`);
          this.dataset.ldsQuickPreviewState = 'ready';
        }
        this._scheduleFlowCheck();
      } catch {
        this._fallback('render-error');
      }
    }

    _showBadgeOriginal() {
      if (!this._sourceImage) return;
      this._sourceImage.hidden = false;
      if (this._canvas) this._canvas.hidden = true;
      if (this._badgeNativeImage) {
        for (const [attribute, value] of Object.entries(this._badgeNativeImage)) {
          if (value !== null && value !== undefined) this._sourceImage.setAttribute(attribute, value);
          else this._sourceImage.removeAttribute(attribute);
        }
      }
    }

    _drawBadge(colorKey, timestamp) {
      if (!this._ready || !this._badgePrepared) return;
      const { base, mask, width, height, pixels } = this._badgePrepared;
      const flowing = colorKey === 'FLOW';
      const rgb = flowing ? null : BADGE_STATIC_COLORS[colorKey] || BADGE_STATIC_COLORS.WHITE;
      if (flowing && !Number.isFinite(timestamp)) timestamp = performance.now();
      const output = new Uint8ClampedArray(pixels);
      const sourceRGB = this._config.sourceRGB;
      for (let index = 0; index < output.length; index += 4) {
        const core = mask[index] / 255;
        const halo = mask[index + 1] / 255;
        if (!core && !halo) continue;
        let color = rgb;
        if (flowing) {
          const localX = (index / 4) % width;
          const flowHue = ((localX / (this._config.legacy ? 350 : width * 350 / 427) - timestamp / 6500) % 1 + 1) % 1;
          color = hue(flowHue);
        }
        for (let channel = 0; channel < 3; channel += 1) {
          output[index + channel] = pixels[index + channel] + (color[channel] - sourceRGB[channel]) * core + color[channel] * halo;
        }
      }
      const frame = new ImageData(output, width, height);
      this._ctx.clearRect(0, 0, this._canvas.width, this._canvas.height);
      this._ctx.drawImage(base, 0, 0, this._canvas.width, this._canvas.height);
      this._ctx.putImageData(frame, this._bbox[0], this._bbox[1]);
    }

    _bodyKey(variant) {
      return this._config.bodyMask
        ? normalize(variant?.options?.[integer(this._config.bodyIndex) ?? 0])
        : null;
    }

    _applyBody(bodyKey) {
      if (bodyKey === this._bodyKeyApplied && this._frameImage) return;
      const { width, height } = this._source;
      const frame = this._ctx.createImageData(width, height);
      frame.data.set(this._baseFrame);
      if (this._config.bodyMask && bodyKey !== 'BLACK') {
        const color = BODY_COLORS[bodyKey];
        if (!color) throw new Error('unknown body color');
        for (let index = 0; index < this._bodyPixels.length; index += 1) {
          const pixel = this._bodyPixels[index];
          const alpha = this._bodyCoverage[index];
          const shade = this._bodyShades[index];
          for (let channel = 0; channel < 3; channel += 1) {
            frame.data[pixel + channel] = Math.round(this._baseFrame[pixel + channel] * (1 - alpha) + color[channel] * shade * alpha);
          }
        }
      }
      this._frameImage = frame;
      this._patchBaseImage = this._ctx.createImageData(this._bbox[2], this._bbox[3]);
      this._copyPatch(frame.data, this._patchBaseImage.data);
      this._patchImage = this._ctx.createImageData(this._bbox[2], this._bbox[3]);
      this._computeLightShades();
      this._ctx.putImageData(frame, 0, 0);
      this._bodyKeyApplied = bodyKey;
    }

    _copyPatch(source, target) {
      const [x, y, width, height] = this._bbox;
      const sourceWidth = this._source.naturalWidth;
      for (let row = 0; row < height; row += 1) {
        const sourceStart = ((y + row) * sourceWidth + x) * 4;
        target.set(source.subarray(sourceStart, sourceStart + width * 4), row * width * 4);
      }
    }

    _computeLightShades() {
      const source = this._patchBaseImage.data;
      this._lightShades = new Float32Array(this._lightPixels.length);
      for (let index = 0; index < this._lightPixels.length; index += 1) {
        const pixel = this._lightPixels[index];
        this._lightShades[index] = this._config.tone === 'lit'
          ? Math.max(source[pixel], source[pixel + 1], source[pixel + 2]) / 255
          : 0.35 + 0.65 * (0.2126 * source[pixel] + 0.7152 * source[pixel + 1] + 0.0722 * source[pixel + 2]) / 255;
      }
    }

    _draw(timestamp) {
      if (this._kind === 'badge') {
        this._drawBadge(this._badgeColorKey(), this._badgeColorKey() === 'FLOW' ? timestamp : null);
        return;
      }
      if (!this._ready || !this._frameImage || !this._patchImage) return;
      const colorKey = normalize(this._variant?.options?.[integer(this._config.colorIndex) ?? 0]);
      const flowing = colorKey === 'FLOW';
      const color = LIGHT_COLORS[colorKey] || LIGHT_COLORS.WHITE;
      const patch = this._patchImage.data;
      patch.set(this._patchBaseImage.data);
      const palette = flowing ? Array.from({ length: this._bbox[2] }, (_, x) => hue((x / this._bbox[2] + timestamp / 4000) % 1)) : null;

      for (let index = 0; index < this._lightPixels.length; index += 1) {
        const pixel = this._lightPixels[index];
        const alpha = this._lightAlpha[index];
        const x = (pixel / 4) % this._bbox[2];
        const rgb = flowing ? palette[x] : color;
        const shade = alpha < 1 && this._config.bodyHaloOverlap
          ? Math.max(this._patchBaseImage.data[pixel], this._patchBaseImage.data[pixel + 1], this._patchBaseImage.data[pixel + 2]) / 255
          : this._lightShades[index];
        for (let channel = 0; channel < 3; channel += 1) {
          patch[pixel + channel] = this._config.tone === 'lit' && colorKey === 'WHITE'
            ? this._patchBaseImage.data[pixel + channel]
            : Math.round(this._patchBaseImage.data[pixel + channel] * (1 - alpha) + rgb[channel] * shade * alpha);
        }
      }
      if (this._config.kitViews) this._ctx.putImageData(this._frameImage, 0, 0);
      this._ctx.putImageData(this._patchBaseImage, this._bbox[0], this._bbox[1]);
      this._ctx.putImageData(this._patchImage, this._bbox[0], this._bbox[1]);
      projectKit(this._canvas, this._config, this._variant);
      if(this._config.kitPartCount)this._canvas.dataset.ldsKitCount=String(this._config.kitPartCount);
    }

    _loadAssets(generation) {
      const revision=++this._kitAssetRevision;
      const cacheKey=this._config.source+'|'+this._config.mask;
      let request=this._kitAssetCache.get(cacheKey);
      if(!request){request=Promise.all([imageFromUrl(this._config.source,this._config.exactRaster===true),imageFromUrl(this._config.mask,this._config.exactRaster===true),this._config.bodyMask?imageFromUrl(this._config.bodyMask):Promise.resolve(null)]);this._kitAssetCache.set(cacheKey,request);}
      request.then(([source,mask,bodyMask])=>{
        if (!this.isConnected || generation !== this._generation || revision!==this._kitAssetRevision) return;
        this._prepareAssets(source, mask, bodyMask);
        this._ready = true;
        this.dataset.ldsQuickPreviewState = 'ready-assets';
        this._syncVariant(this._variant || this._variantFromPicker());
        this._variantFrameDeadline = performance.now() + 1000;
        this._placeVariantFrame();
      }).catch(() => {
        if (generation === this._generation && revision===this._kitAssetRevision) this._fallback('asset-error');
      });
    }

    _prepareAssets(source, mask, bodyMask) {
      if (this._kind === 'badge') {
        this._prepareBadgeAssets(source, mask);
        return;
      }
      const configuredSize = Array.isArray(this._config.imageSize) ? this._config.imageSize.map(integer) : [];
      if (configuredSize.length !== 2 || configuredSize.some(value => !value || value < 1)) throw new Error('invalid source size');
      if (source.naturalWidth !== configuredSize[0] || source.naturalHeight !== configuredSize[1]) throw new Error('source size mismatch');

      const bbox = Array.isArray(this._config.bbox) ? this._config.bbox.map(integer) : [];
      if (bbox.length !== 4 || bbox.some(value => value === null) || bbox[0] < 0 || bbox[1] < 0 || bbox[2] < 1 || bbox[3] < 1 || bbox[0] + bbox[2] > source.naturalWidth || bbox[1] + bbox[3] > source.naturalHeight) throw new Error('invalid preview bounds');
      if (mask.naturalWidth !== bbox[2] || mask.naturalHeight !== bbox[3]) throw new Error('light mask geometry mismatch');

      const stem = String(this._config.expectedMediaStem || '').trim().toLowerCase();
      const guardImage = this._config.variantKitSourceGuard ? this._slides.map(slide => slide.querySelector('img')).find(image => image && String(image.currentSrc || image.src || '').toLowerCase().includes(stem)) : this._sourceImage;
      if (!guardImage) throw new Error('selected kit gallery source missing');
      const originalUrl = String(guardImage.currentSrc || guardImage.src || '').toLowerCase();
      if (stem && !originalUrl.includes(stem)) throw new Error('gallery source mismatch');
      if (guardImage.naturalWidth && guardImage.naturalHeight) {
        const galleryRatio = guardImage.naturalWidth / guardImage.naturalHeight;
        const sourceRatio = source.naturalWidth / source.naturalHeight;
        if (Math.abs(galleryRatio - sourceRatio) > 0.02) throw new Error('gallery ratio mismatch');
      }

      this._source = source;
      this._frameImage=null;this._bodyKeyApplied=null;
      this._bbox = bbox;
      this._canvas.width = source.naturalWidth;
      this._canvas.height = source.naturalHeight;
      this._ctx = this._canvas.getContext('2d', { willReadFrequently: true });
      if (!this._ctx) throw new Error('canvas unavailable');
      this._ctx.drawImage(source, 0, 0);
      this._baseFrame = new Uint8ClampedArray(this._ctx.getImageData(0, 0, source.naturalWidth, source.naturalHeight).data);

      const maskData = this._readAsset(mask, bbox[2], bbox[3]);
      const lightPixels = [];
      const lightAlpha = [];
      for (let pixel = 0; pixel < maskData.length; pixel += 4) {
        if (!maskData[pixel + 3]) continue;
        lightPixels.push(pixel);
        lightAlpha.push(maskData[pixel + 3] / 255);
      }
      this._lightPixels = new Uint32Array(lightPixels);
      this._lightAlpha = new Float32Array(lightAlpha);

      this._bodyPixels = new Uint32Array(0);
      this._bodyCoverage = new Float32Array(0);
      this._bodyShades = new Float32Array(0);
      if (bodyMask) {
        if (bodyMask.naturalWidth !== source.naturalWidth || bodyMask.naturalHeight !== source.naturalHeight) throw new Error('body mask geometry mismatch');
        const bodyData = this._readAsset(bodyMask, source.naturalWidth, source.naturalHeight);
        const bodyPixels = [];
        const bodyCoverage = [];
        const bodyShades = [];
        for (let pixel = 0; pixel < bodyData.length; pixel += 4) {
          if (!bodyData[pixel + 3]) continue;
          bodyPixels.push(pixel);
          bodyCoverage.push(bodyData[pixel + 3] / 255);
          bodyShades.push(bodyData[pixel] / 255);
        }
        this._bodyPixels = new Uint32Array(bodyPixels);
        this._bodyCoverage = new Float32Array(bodyCoverage);
        this._bodyShades = new Float32Array(bodyShades);
      }
    }

    _prepareBadgeAssets(source, mask) {
      const configuredSize = Array.isArray(this._config.imageSize) ? this._config.imageSize.map(integer) : [];
      if (configuredSize.length !== 2 || configuredSize.some(value => !value || value < 1)) throw new Error('invalid badge source size');
      if (source.naturalWidth !== configuredSize[0] || source.naturalHeight !== configuredSize[1]) throw new Error('badge source size mismatch');

      const bbox = Array.isArray(this._config.bbox) ? this._config.bbox.map(integer) : [];
      if (bbox.length !== 4 || bbox.some(value => value === null) || bbox[0] < 0 || bbox[1] < 0 || bbox[2] < 1 || bbox[3] < 1 || bbox[0] + bbox[2] > source.naturalWidth || bbox[1] + bbox[3] > source.naturalHeight) throw new Error('invalid badge preview bounds');
      if (mask.naturalWidth !== bbox[2] || mask.naturalHeight !== bbox[3]) throw new Error('badge mask geometry mismatch');

      const stem = String(this._config.expectedMediaStem || '').trim().toLowerCase();
      const originalUrl = String(this._sourceImage.currentSrc || this._sourceImage.src || '').toLowerCase();
      if (stem && !originalUrl.includes(stem)) throw new Error('gallery source mismatch');
      if (this._sourceImage.naturalWidth && this._sourceImage.naturalHeight) {
        const galleryRatio = this._sourceImage.naturalWidth / this._sourceImage.naturalHeight;
        const sourceRatio = source.naturalWidth / source.naturalHeight;
        if (Math.abs(galleryRatio - sourceRatio) > 0.02) throw new Error('gallery ratio mismatch');
      }

      const crop = document.createElement('canvas');
      crop.width = bbox[2];
      crop.height = bbox[3];
      const cropContext = crop.getContext('2d', { willReadFrequently: true });
      if (!cropContext) throw new Error('badge crop canvas unavailable');
      cropContext.drawImage(source, bbox[0], bbox[1], bbox[2], bbox[3], 0, 0, bbox[2], bbox[3]);
      const pixels = cropContext.getImageData(0, 0, bbox[2], bbox[3]).data;
      const maskData = this._readAsset(mask, bbox[2], bbox[3]);

      this._source = source;
      this._bbox = bbox;
      this._canvas.width = source.naturalWidth;
      this._canvas.height = source.naturalHeight;
      this._ctx = this._canvas.getContext('2d', { willReadFrequently: true });
      if (!this._ctx) throw new Error('canvas unavailable');
      this._badgePrepared = { base: source, mask: maskData, width: bbox[2], height: bbox[3], pixels };
      this._badgeWhiteSource = !this._config.legacy && this._config.sourceRGB.every(value => Number(value) === 255);
      this._badgeNativeImage = {
        src: this._sourceImage.getAttribute('src'),
        srcset: this._sourceImage.getAttribute('srcset'),
        sizes: this._sourceImage.getAttribute('sizes'),
        alt: this._sourceImage.getAttribute('alt'),
      };
    }

    _readAsset(image, width, height) {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) throw new Error('asset canvas unavailable');
      context.drawImage(image, 0, 0, width, height);
      return context.getImageData(0, 0, width, height).data;
    }

    _modalIsVisible() {
      if (!this._dialog?.open || document.visibilityState !== 'visible') return false;
      const style = getComputedStyle(this._dialog);
      if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
      const rect = this._dialog.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    }

    _slideIsVisible() {
      if (!this._slide || this._slide.hidden || this._slide.closest('[hidden]')) return false;
      const style = getComputedStyle(this._slide);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
      const slideRect = this._slide.getBoundingClientRect();
      if (!slideRect.width || !slideRect.height) return false;
      const selected = this._slide.classList.contains('is-in-view') || this._slide.classList.contains('is-selected') || this._slide.classList.contains('is-active');
      if (selected) return true;
      if (this._slides.some(slide => slide.classList.contains('is-in-view') || slide.classList.contains('is-selected') || slide.classList.contains('is-active'))) return false;
      const dialogRect = this._dialog.getBoundingClientRect();
      return slideRect.width > 0 && slideRect.height > 0 && slideRect.right > dialogRect.left && slideRect.left < dialogRect.right && slideRect.bottom > dialogRect.top && slideRect.top < dialogRect.bottom;
    }

    _canAnimate() {
      if (this._kind === 'badge' && this._badgeClosing) return false;
      if (this._kind === 'badge') {
        return this._badgeColorKey() === 'FLOW' && this._ready && this._isSupported(this._variant) && !this._canvas?.hidden && !matchMedia('(prefers-reduced-motion: reduce)').matches && this._modalIsVisible() && this._slideIsVisible();
      }
      const color = normalize(this._variant?.options?.[integer(this._config.colorIndex) ?? 0]);
      return color === 'FLOW' && this._ready && this._isSupported(this._variant) && !this._canvas?.hidden && !matchMedia('(prefers-reduced-motion: reduce)').matches && this._modalIsVisible() && this._slideIsVisible();
    }

    _scheduleFlowCheck() {
      if (this._kind === 'badge') {
        this._scheduleBadgeFlowCheck();
        return;
      }
      if (!this.isConnected || !this._ready) return;
      clearTimeout(this._flowRetry);
      this._flowRetry = setTimeout(() => {
        this._flowRetry = 0;
        if (this._canAnimate()) this._startFlow();
        else this._stopFlow();
        const flow = this._variant && normalize(this._variant.options?.[integer(this._config.colorIndex) ?? 0]) === 'FLOW';
        const slideCouldReappear = this._slide && !this._slide.hidden && !this._slide.closest('[hidden]');
        if (flow && slideCouldReappear && this._dialog?.open && document.visibilityState === 'visible' && this.isConnected) this._scheduleFlowCheck();
      }, 120);
    }

    _scheduleBadgeFlowCheck() {
      if (!this.isConnected || !this._ready || this._badgeClosing) return;
      clearTimeout(this._flowRetry);
      this._flowRetry = setTimeout(() => {
        this._flowRetry = 0;
        if (this._canAnimate()) this._startFlow();
        else this._stopFlow();
        const flow = this._variant && this._badgeColorKey() === 'FLOW';
        const slideCouldReappear = this._slide && !this._slide.hidden && !this._slide.closest('[hidden]');
        if (flow && slideCouldReappear && this._dialog?.open && document.visibilityState === 'visible' && this.isConnected) this._scheduleBadgeFlowCheck();
      }, 120);
    }

    _startFlow() {
      if (this._raf || !this._canAnimate()) return;
      const tick = timestamp => {
        this._raf = 0;
        if (!this._canAnimate()) {
          this._stopFlow();
          this._scheduleFlowCheck();
          return;
        }
        if (timestamp - this._lastFrame >= 32) {
          this._draw(timestamp);
          this._lastFrame = timestamp;
        }
        this._raf = requestAnimationFrame(tick);
      };
      this._raf = requestAnimationFrame(tick);
    }

    _stopFlow() {
      if (this._raf) cancelAnimationFrame(this._raf);
      this._raf = 0;
    }

    _hideCanvas() {
      if (this._kind === 'badge' && this._sourceImage) this._showBadgeOriginal();
      if (this._canvas) this._canvas.hidden = true;
      this.dataset.ldsQuickPreviewState = this._ready ? 'original' : 'loading';
    }

    _fallback(reason) {
      this._hideCanvas();
      if (reason !== 'loading') this._removeCanvas();
      this.dataset.ldsQuickPreviewState = `fallback-${reason}`;
    }

    _removeCanvas() {
      this._canvas?.remove();
      this._canvas = null;
      if (this._frameHost && this._previousPosition !== undefined) {
        this._frameHost.style.position = this._previousPosition;
        this._previousPosition = undefined;
      }
    }

    _teardown() {
      this._ready=false;this._kitAssetRevision+=1;this._kitAssetCache?.clear();
      if(this._kitBaseConfig?.kitSources){this._source=null;this._baseFrame=null;this._frameImage=null;this._patchImage=null;this._patchBaseImage=null;}
      this._abort?.abort();
      this._abort = null;
      this._observer?.disconnect();
      this._observer = null;
      this._stopFlow();
      clearTimeout(this._flowRetry);
      this._flowRetry = 0;
      this._removeCanvas();
      this._frameHost = null;
    }
  }

  customElements.define(TAG, QuickPreview);
})();
